import { Capability } from "@/types/Capability";
import { Plugin } from "@/types/enums/Plugin";
import { resolveCapabilities } from "@/lib/authz/capabilities";

/**
 * Role-aware landing + page-list resolution for surfaces that link INTO an
 * event from outside its CapabilitiesProvider (the dashboard event table and
 * the ⌘K command palette).
 *
 * Both surfaces historically assumed full access — they linked every event row
 * to `/guests/guestlist` and listed every sub-page regardless of the user's
 * role. For CUSTOM "individual" roles that meant landing on (or jumping to) a
 * locked-door page the role can't enter. These helpers compute, from a resolved
 * capability set + the event's enabled plugins, the first section the role may
 * actually open and the full set of openable pages — mirroring the gates in
 * `useEventSidebar` so the three surfaces stay consistent.
 */

type SectionGate = {
  /** Capability(ies) that grant access — any one suffices. */
  caps: Capability[];
  /**
   * Capabilities the DESTINATION additionally guards on. `slotting` is a
   * redirect into the programme surface, whose page guard demands
   * `event-program:read` — landing a slotting-only role there hands it
   * SNoAccess as the event's front door.
   */
  alsoRequires?: Capability[];
  /** Plugin required for the section to exist at all (omit = always present). */
  plugin?: Plugin;
  /** Any one of these plugins is enough (programme hosts slotting too). */
  anyPlugin?: Plugin[];
  /** Relative sub-path under `/event/:id/`. */
  path: string;
};

/**
 * Order = landing PRIORITY. Guestlist is first to preserve the historical
 * default (`/guests/guestlist`): any role that can open it lands exactly where
 * it always did — only roles that CAN'T are redirected, so this change is
 * invisible to the OWNER/ADMIN/EDITOR/VIEWER majority. The remaining entries
 * follow the default `useEventSidebar` order. Per-event custom `sidebarOrder`
 * is OWNER/ADMIN-only (full access), so it never affects a restricted landing.
 */
const SECTION_GATES: SectionGate[] = [
  // Historical default first. guestlist needs guests:read; a requests-only role
  // (companions read but no guests:read) lands on requests instead.
  { caps: ["guests:read"], path: "guests/guestlist" },
  { caps: ["guests:read.companions"], path: "guests/requests" },
  { caps: ["dashboards:read"], plugin: Plugin.DASHBOARD, path: "dashboard" },
  { caps: ["event-goals:read"], plugin: Plugin.GOALS, path: "goals" },
  { caps: ["website:read"], plugin: Plugin.WEBSITE, path: "website" },
  { caps: ["check-in-logs:read"], plugin: Plugin.CHECK_IN, path: "check-in/history" },
  { caps: ["check-in-devices:read"], plugin: Plugin.CHECK_IN, path: "check-in/devices" },
  {
    caps: ["slotting:read"],
    alsoRequires: ["event-program:read"],
    plugin: Plugin.SLOTTING,
    path: "slotting",
  },
  { caps: ["seating:read"], plugin: Plugin.SEATING, path: "seating" },
  { caps: ["attachments:read"], plugin: Plugin.ATTACHMENTS, path: "attachments" },
  { caps: ["documents:read"], plugin: Plugin.DOCUMENTS, path: "documents" },
  // Content pages — one plugin per entity page (Media lives under settings,
  // which has no landing gate by design; see the settings note below).
  // Programme hosts the slotting boards too, so its gate widens like the
  // sidebar's (PROGRAM || SLOTTING).
  {
    caps: ["event-program:read"],
    anyPlugin: [Plugin.PROGRAM, Plugin.SLOTTING],
    path: "programme",
  },
  { caps: ["event-speakers:read"], plugin: Plugin.SPEAKERS, path: "speakers" },
  { caps: ["event-sponsors:read"], plugin: Plugin.SPONSORS, path: "sponsors" },
  { caps: ["workflows:read"], plugin: Plugin.ACTIONS, path: "automation/workflows" },
  { caps: ["email-templates:read"], plugin: Plugin.MESSAGING, path: "messaging/mail-templates" },
  // NOTE: no settings/* gate here. settings/general|rules|plugins enforce a hard
  // role ∈ {OWNER,ADMIN} guard (not just a capability), so landing a non-admin
  // there would bounce to SNoAccess. OWNER/ADMIN always match an earlier section
  // gate (they hold every read cap), so a settings landing is never needed; any
  // role with no readable section falls through to the isCustom/guestlist
  // fallbacks below (settings/access is always reachable for CUSTOM).
];

function hasPlugin(plugins: string[], plugin?: Plugin): boolean {
  return !plugin || plugins.includes(plugin);
}

function gateOpen(
  gate: SectionGate,
  caps: ReadonlySet<Capability>,
  plugins: string[],
): boolean {
  if (gate.anyPlugin && !gate.anyPlugin.some((p) => plugins.includes(p))) return false;
  if (!hasPlugin(plugins, gate.plugin)) return false;
  if (gate.alsoRequires && !gate.alsoRequires.every((c) => caps.has(c))) return false;
  return gate.caps.some((c) => caps.has(c));
}

/**
 * The first event sub-path (relative to `/event/:id/`) the given capability set
 * may open, honouring enabled plugins. CUSTOM users with no section capability
 * but an event-role of CUSTOM still get a home: the Access page renders their
 * read-only "My access" view, so `isCustom` falls back there. Everyone else
 * falls back to the guestlist (the legacy default) — VIEWER and up always hold
 * `guests:read`, so this only bites a pathological empty-cap non-CUSTOM role.
 */
export function firstAccessibleEventSubPath(
  caps: ReadonlySet<Capability>,
  plugins: string[],
  isCustom: boolean,
): string {
  for (const gate of SECTION_GATES) {
    if (gateOpen(gate, caps, plugins)) return gate.path;
  }
  if (isCustom) return "settings/access";
  return "guests/guestlist";
}

/**
 * Resolve an event's effective capabilities from the lightweight `role` (+
 * optional server-provided CUSTOM `capabilities`) carried by the dashboard /
 * search event rows. Non-CUSTOM roles resolve purely client-side via the
 * IMPLICIT_CAPS table; CUSTOM relies on the backend-projected list (absent →
 * empty set → fail-closed).
 */
export function resolveEventCaps(
  role: string,
  capabilities?: Capability[] | null,
): ReadonlySet<Capability> {
  return resolveCapabilities({ role, capabilities });
}

/**
 * Absolute first-accessible URL for an event, ready to drop into an anchor or
 * router push. Mirrors `firstAccessibleEventSubPath` but resolves caps from the
 * row-level role first.
 */
export function firstAccessibleEventUrl(
  eventId: string,
  role: string,
  plugins: string[],
  capabilities?: Capability[] | null,
): string {
  const caps = resolveEventCaps(role, capabilities);
  const sub = firstAccessibleEventSubPath(caps, plugins, role === "CUSTOM");
  return `/event/${eventId}/${sub}`;
}

/** A ⌘K page entry, gated by the capabilities its destination guards on. */
export type GatedPage = {
  /** Relative sub-path under `/event/:id/`. */
  path: string;
  /** Any one of these caps grants access. Empty = ungated (always shown). */
  caps: Capability[];
  /** Plugin required for the page to exist (omit = always). */
  plugin?: Plugin;
  /** Any one of these plugins is enough (programme hosts slotting too) —
   *  the same widening as `SectionGate.anyPlugin`. */
  anyPlugin?: Plugin[];
};

/**
 * Decide whether a single ⌘K event page should be offered to the given role.
 * Ungated pages (empty `caps`) always show provided their plugin is enabled.
 */
export function isPageVisible(
  page: GatedPage,
  caps: ReadonlySet<Capability>,
  plugins: string[],
): boolean {
  if (page.anyPlugin && !page.anyPlugin.some((p) => plugins.includes(p))) return false;
  if (!hasPlugin(plugins, page.plugin)) return false;
  if (page.caps.length === 0) return true;
  return page.caps.some((c) => caps.has(c));
}
